/**
 * Constraint Panel Component
 * 
 * Shows leveling constraint violations grouped by type,
 * with quick actions to fix each violation.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  ArrowRight,
  GitBranch,
  FileText,
  Target,
  Layers,
} from 'lucide-react';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import {
  validateAllLevelingConstraints,
} from '../../../lib/altitudeValidation';
import { CONSTRAINT_TYPE_LABELS } from '../../../types/prioritization/leveling';
import type { LevelingConstraintViolation, LevelingConstraintType } from '../../../types/prioritization/leveling';

// ============================================================================
// Types
// ============================================================================

interface ConstraintPanelProps {
  /** Filter to specific item IDs */
  itemIds?: string[];
  /** Show in compact mode */
  compact?: boolean;
  /** Callback when user clicks fix action */
  onFixViolation?: (violation: LevelingConstraintViolation) => void;
}

// ============================================================================
// Icons for constraint types
// ============================================================================

const CONSTRAINT_TYPE_ICONS: Record<LevelingConstraintType, React.ReactNode> = {
  standalone_violation: <GitBranch className="h-5 w-5" />,
  completion_missing: <FileText className="h-5 w-5" />,
  altitude_mismatch: <Target className="h-5 w-5" />,
  parent_gap_violation: <Layers className="h-5 w-5" />,
  atomic_violation: <AlertTriangle className="h-5 w-5" />,
};

// ============================================================================
// Sub-components
// ============================================================================

function ViolationCard({ 
  violation, 
  onFix 
}: { 
  violation: LevelingConstraintViolation;
  onFix?: () => void;
}) {
  const isError = violation.severity === 'error';
  
  return (
    <div className={`
      p-4  border
      ${isError 
        ? 'bg-[var(--coral-dim)] border-[var(--coral)]' 
        : 'bg-[var(--mustard-dim)] border-[var(--mustard)]'
      }
    `}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className={`
            p-1.5 
            ${isError ? 'bg-[var(--coral-dim)] text-[var(--coral)]' : 'bg-[var(--mustard-dim)] text-[var(--mustard)]'}
          `}>
            {isError ? (
              <AlertCircle className="h-4 w-4" />
            ) : (
              <AlertTriangle className="h-4 w-4" />
            )}
          </div>
          <div>
            <Link 
              to={`/prioritization/items/${violation.itemId}`}
              className="font-medium text-[var(--text-pri)] hover:text-primary-600"
            >
              {violation.itemTitle}
            </Link>
            <p className={`text-sm mt-0.5 ${isError ? 'text-[var(--coral)]' : 'text-[var(--mustard)]'}`}>
              {violation.message}
            </p>
            {violation.suggestedAction && (
              <p className="text-sm text-[var(--text-sec)] mt-1">
                <span className="font-medium">Suggested:</span> {violation.suggestedAction}
              </p>
            )}
          </div>
        </div>
        
        {onFix && (
          <button
            onClick={onFix}
            className={`
              flex items-center gap-1 px-3 py-1.5 text-sm  transition-colors
              ${isError 
                ? 'bg-[var(--coral-dim)] text-[var(--coral)] hover:bg-[var(--coral-dim)]' 
                : 'bg-[var(--mustard-dim)] text-[var(--mustard)] hover:bg-[var(--mustard-dim)]'
              }
            `}
          >
            Fix
            <ArrowRight className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
}

function ConstraintTypeSection({
  type,
  violations,
  onFix,
}: {
  type: LevelingConstraintType;
  violations: LevelingConstraintViolation[];
  onFix?: (violation: LevelingConstraintViolation) => void;
}) {
  if (violations.length === 0) return null;
  
  const errorCount = violations.filter(v => v.severity === 'error').length;
  const warningCount = violations.filter(v => v.severity === 'warning').length;
  
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-[var(--hover-tint)] text-[var(--text-sec)]">
          {CONSTRAINT_TYPE_ICONS[type]}
        </div>
        <div>
          <h3 className="font-medium text-[var(--text-pri)]">
            {CONSTRAINT_TYPE_LABELS[type]}
          </h3>
          <p className="text-sm text-[var(--text-ter)]">
            {errorCount > 0 && <span className="text-[var(--coral)]">{errorCount} error{errorCount !== 1 ? 's' : ''}</span>}
            {errorCount > 0 && warningCount > 0 && ' · '}
            {warningCount > 0 && <span className="text-[var(--mustard)]">{warningCount} warning{warningCount !== 1 ? 's' : ''}</span>}
          </p>
        </div>
      </div>
      
      <div className="space-y-2 pl-11">
        {violations.map((violation) => (
          <ViolationCard
            key={`${violation.itemId}-${violation.constraintType}`}
            violation={violation}
            onFix={onFix ? () => onFix(violation) : undefined}
          />
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function ConstraintPanel({
  itemIds,
  compact = false,
  onFixViolation,
}: ConstraintPanelProps) {
  const items = useItemsStore(state => state.items);
  
  // Filter candidates if itemIds provided
  const filteredCandidates = useMemo(() => {
    if (!itemIds) return items;
    const idSet = new Set(itemIds);
    return items.filter(c => idSet.has(c.id));
  }, [items, itemIds]);
  
  // Validate all candidates
  const validationResult = useMemo(() => {
    return validateAllLevelingConstraints(filteredCandidates);
  }, [filteredCandidates]);
  
  const { violations, byType, summary } = validationResult;
  
  // No violations - show success state
  if (violations.length === 0) {
    return (
      <div className="bg-[var(--green-dim)] border border-[var(--green)] p-6 text-center">
        <CheckCircle className="h-12 w-12 text-[var(--green)] mx-auto mb-3" />
        <h3 className="font-medium text-[var(--green)] dark:text-[var(--text-ter)]">All Constraints Satisfied</h3>
        <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)] mt-1">
          {filteredCandidates.length} items checked, no violations found.
        </p>
      </div>
    );
  }
  
  // Compact mode - just show summary
  if (compact) {
    return (
      <div className={`
        p-4  border
        ${summary.errors > 0 ? 'bg-[var(--coral-dim)] border-[var(--coral)]' : 'bg-[var(--mustard-dim)] border-[var(--mustard)]'}
      `}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {summary.errors > 0 ? (
              <AlertCircle className="h-5 w-5 text-[var(--coral)]" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-[var(--mustard)]" />
            )}
            <div>
              <p className="font-medium text-[var(--text-pri)]">
                {summary.total} Constraint Violation{summary.total !== 1 ? 's' : ''}
              </p>
              <p className="text-sm text-[var(--text-sec)]">
                {summary.errors > 0 && <span className="text-[var(--coral)]">{summary.errors} error{summary.errors !== 1 ? 's' : ''}</span>}
                {summary.errors > 0 && summary.warnings > 0 && ', '}
                {summary.warnings > 0 && <span className="text-[var(--mustard)]">{summary.warnings} warning{summary.warnings !== 1 ? 's' : ''}</span>}
              </p>
            </div>
          </div>
          <Link
            to="/prioritization/leveling/constraints"
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            View All
          </Link>
        </div>
      </div>
    );
  }
  
  // Full panel with all violations
  return (
    <div className="space-y-6">
      {/* Header with summary */}
      <div className={`
        p-4  border
        ${summary.errors > 0 ? 'bg-[var(--coral-dim)] border-[var(--coral)]' : 'bg-[var(--mustard-dim)] border-[var(--mustard)]'}
      `}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {summary.errors > 0 ? (
              <AlertCircle className="h-6 w-6 text-[var(--coral)]" />
            ) : (
              <AlertTriangle className="h-6 w-6 text-[var(--mustard)]" />
            )}
            <div>
              <h2 className="font-semibold text-[var(--text-pri)]">
                {summary.total} Constraint Violation{summary.total !== 1 ? 's' : ''}
              </h2>
              <p className="text-sm text-[var(--text-sec)]">
                {filteredCandidates.length} items checked
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-[var(--coral-dim)] text-[var(--coral)] text-sm rounded-full">
              {summary.errors} errors
            </span>
            <span className="px-2 py-1 bg-[var(--mustard-dim)] text-[var(--mustard)] text-sm rounded-full">
              {summary.warnings} warnings
            </span>
          </div>
        </div>
      </div>
      
      {/* Violations by type */}
      <div className="space-y-6">
        <ConstraintTypeSection
          type="standalone_violation"
          violations={byType.standalone_violation}
          onFix={onFixViolation}
        />
        <ConstraintTypeSection
          type="completion_missing"
          violations={byType.completion_missing}
          onFix={onFixViolation}
        />
        <ConstraintTypeSection
          type="parent_gap_violation"
          violations={byType.parent_gap_violation}
          onFix={onFixViolation}
        />
        <ConstraintTypeSection
          type="atomic_violation"
          violations={byType.atomic_violation}
          onFix={onFixViolation}
        />
        <ConstraintTypeSection
          type="altitude_mismatch"
          violations={byType.altitude_mismatch}
          onFix={onFixViolation}
        />
      </div>
    </div>
  );
}

export default ConstraintPanel;
